import { Navigate, useSearchParams } from "react-router";
import { paths } from "@shared-core/routing/routes";
import {
  usePortalReviewScenarios,
  usePortalSignInAdapter,
} from "@shared-core/data/PortalSignInAdapterContext";
import { useListQuery } from "@shared-core/domain/useListQuery";
import type { PortalSignInOutcome } from "@shared-core/data/customer-portal-access-types";
import { PortalReviewBar } from "./PortalReviewBar";
import { outcomePath } from "./outcomePath";
import {
  PortalSignInFailed,
  PortalSignInLoading,
} from "./PortalSignInProgress";

/**
 * Loads the sign-in outcome for a page that shows one kind of it. If the
 * outcome is a different kind (someone followed an old link, or a state moved
 * on), it sends them to the page that shows it, so a person is never left
 * looking at the wrong state. `ready` has no page of its own: it goes into the
 * portal.
 *
 * The outcome is the server's (`GET /customer-api/session/customers`). `scenario`
 * names a review fixture and exists only in the mock build, where the review bar
 * jumps between states; the real build has no scenarios and ignores the parameter.
 */
export function PortalSignInOutcomeGate<K extends PortalSignInOutcome["kind"]>({
  kinds,
  children,
}: {
  /** The outcome kinds this page shows. Any other is sent to its own page. */
  kinds: readonly K[];
  children: (
    outcome: Extract<PortalSignInOutcome, { kind: K }>,
    scenario: string | undefined,
  ) => React.ReactNode;
}) {
  const adapter = usePortalSignInAdapter();
  const review = usePortalReviewScenarios();
  const [searchParams] = useSearchParams();
  const scenario = review
    ? (searchParams.get("scenario") ?? undefined)
    : undefined;
  const { state, retry } = useListQuery(async () => {
    if (review && scenario) review.selectScenario(scenario);
    return adapter.getSignInOutcome();
  }, [adapter, review, scenario]);

  if (state.status === "loading") {
    return (
      <PortalSignInLoading footer={review ? <PortalReviewBar /> : undefined} />
    );
  }
  if (state.status === "error") {
    // A lapsed sign-in is a 401: the page has nothing to show, so back to sign-in.
    if ((state.error as { status?: unknown } | null)?.status === 401)
      return <Navigate to={paths.portalSignIn()} replace />;
    return <PortalSignInFailed onRetry={retry} />;
  }
  if (!(kinds as readonly string[]).includes(state.data.kind)) {
    return <Navigate to={outcomePath(state.data, scenario)} replace />;
  }
  return (
    <>
      {children(
        state.data as Extract<PortalSignInOutcome, { kind: K }>,
        scenario,
      )}
    </>
  );
}
